/**
 * @file    widgetOptionsBuilder.ts
 * @scope   SHARED
 * @summary Prepares the Actions/Options checkbox descriptors shown in the drawer "Options"
 *          accordion, reading the current values out of the widget.
 * @consumes diyActionsOptions / diyChartOptions strategy tables, `library` aliases
 * @produces `buildChartActions`, `buildChartOptions`, `resolveDecimalPlacesOption`
 * @sideEffects None — every table is deep-cloned before being annotated.
 * @notes The descriptor tables are mutable by design (the UI reads `isChecked`/`value` off them),
 *        hence the `structuredClone` on entry: mutating the imported constants would leak state
 *        between widgets.
 */
import { library } from 'components';

/** Option/action descriptor as consumed by the checkbox components. */
export type OptionDescriptor = any;

/**
 * Returns the action descriptors for `alias` with `isChecked` reflecting the widget's active
 * actions.
 */
export function buildChartActions(
  actionsByChart: any,
  alias: string,
  activeActions: string[] | undefined
): OptionDescriptor[] | undefined {
  const clonedTable = structuredClone(actionsByChart);
  const descriptors = clonedTable?.[alias as keyof typeof clonedTable];
  if (!descriptors) return descriptors;

  activeActions?.forEach((actionName) => {
    const descriptor = descriptors.find((a: any) => a.actionName === actionName);
    if (descriptor) descriptor.isChecked = true;
  });

  return descriptors;
}

/**
 * Returns the option descriptors for `optionsAlias`, annotated with the widget's current values.
 *
 * @param optionsAlias Lookup key (alias, or business rule name for child charts).
 * @param widget The widget being edited; supplies `alias`, `options` and `customWidgetConfig`.
 */
export function buildChartOptions(
  optionsByChart: any,
  optionsAlias: string | undefined,
  widget: any
): OptionDescriptor[] | undefined {
  const { alias, options } = widget;
  const clonedTable = structuredClone(optionsByChart);
  const descriptors = clonedTable?.[optionsAlias as keyof typeof clonedTable];
  if (!descriptors) return descriptors;

  // WHY: data grids store most of their toggles under `options.table`, while a handful of them
  // (delta, line chart, and any option owning a child component) stay at the root.
  const isDataGrid = alias === library.DYNAMIC_TABLE_OLD || alias === library.DYNAMIC_TABLE;

  descriptors.forEach((descriptor: any) => {
    if (typeof descriptor.isChecked === 'undefined') {
      descriptor.value = isDataGrid
        ? resolveDecimalPlacesOption(widget)
        : options[descriptor.actionName]
        ? descriptor.value
        : options[descriptor.actionName];
      return;
    }
    descriptor.isChecked = isDataGrid
      ? resolveDataGridToggle(descriptor, optionsAlias, options)
      : options[descriptor.actionName];
  });

  return descriptors;
}

/** Reads a data-grid toggle from its root-level or `options.table` location. */
function resolveDataGridToggle(
  descriptor: any,
  optionsAlias: string | undefined,
  options: any
): boolean {
  const isRootLevel =
    (optionsAlias === 'OneQuestionOneCalculation' &&
      descriptor.actionName === 'displayTimePeriodDelta') ||
    descriptor.childComponent !== undefined ||
    descriptor.actionName === 'showLineChart';
  return isRootLevel ? options[descriptor.actionName] : options?.table?.[descriptor.actionName];
}

/** Data grids fall back to 2 decimals when no explicit format has been stored. */
function resolveDecimalPlacesOption(widget: any): number {
  return widget?.customWidgetConfig?.decimalFormat !== -1
    ? widget?.customWidgetConfig?.decimalFormat
    : 2;
}

/**
 * Picks the descriptor the decimal-places dropdown must render: the widget's own value when set,
 * otherwise the table default.
 */
export function resolveDecimalPlacesDescriptor(
  widget: any,
  fallback: OptionDescriptor
): OptionDescriptor {
  const { decimalPlaces } = widget.options;
  if (decimalPlaces && decimalPlaces >= 0) {
    return { actionName: 'decimalPlaces', displayName: 'Decimal Places', value: decimalPlaces };
  }
  return fallback;
}
